/*
  THIS IS A TEMPORARY COMPONENT TO BE USED IN A PMF EXPERIMENT

  Given time and current limitations of ProductDescription component we decided to create a temporary hardcoded component to replace the ProductDescription.
  The experiment is expected to run for a couple of weeks, after that we will revaluate the need for a more permanent solution or if we can remove this component entirely.

  Ticket: https://everlyhealth.atlassian.net/browse/VCP-398
*/

import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  theme,
  useDisclosure,
  useMediaQuery,
} from '@everlywell/ui-kit';
import { createSharedButtonProps } from 'components/PageSections/utils';
import Price from 'components/Price';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useInView } from 'react-intersection-observer';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { isBrowser } from 'utils/helpers';
import useMdpModals from 'utils/hooks/useMdpModals';

import BulletPointWithIcon from '../BulletPointWithIcon';

const title = 'UTI Antibiotics Treatment';
const cta = {
  label: 'Get Started',
  url: '/whatever#disclaimer',
  metadata: {
    metadata_id: '',
  },
};

const items: Item[] = [
  {
    label: 'One-time purchase',
    description: 'Get a one-time prescription of UTI Antibiotics',
    price: {
      value: 45,
      frequency: 'One-time',
    },
  },
  {
    label: 'Quarterly Subscription',
    description: 'Get a recurring prescriptions of UTI Antibiotics',
    price: {
      value: 36,
      frequency: 'Quarterly',
      promoText: '20% off',
    },
  },
];

const bulletPoints = [
  {
    iconName: 'WarningCircle',
    iconColor: 'tints.uiGrey',
    text: 'Prescription only, medication cost not included',
    textColor: 'tints.darkGrey',
  },
  {
    iconName: 'Asterisk',
    iconColor: 'tints.uiGrey',
    text: 'Treatment plans available within 24 hours, seven days a week (excluding major holidays). Select states require live telehealth appointments.',
    textColor: 'tints.darkGrey',
  },
];

const modal = {
  heading: 'Thank you for your interest!',
  content:
    'This recurring treatment and bundles are not available yet, but we can still help you access one-time treatment options. Your feedback helps us improve your patient experience. Please click the button below to continue.',
  ctaLabel: 'Proceed with one-time treatment',
};

type Item = {
  label: string;
  description: string;
  price: {
    value: number;
    frequency: string;
    promoText?: string;
  };
};

type OptionsProps = {
  items: Item[];
  selectedValue: Item;
  setSelectedValue: Dispatch<SetStateAction<Item>>;
  sx?: any;
};

type StickyProps = {
  isVisible: boolean;
  price: number;
  cta: {
    label: string;
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
  };
};

type FooterDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  price: number;
  selectedOption: string;
  content?: JSX.Element;
  cta?: JSX.Element;
};

type CTA = Queries.ContentfulCallToAction001 | null | undefined;

type ModalProps = {
  isOpen: boolean;
  modalHandler: Dispatch<SetStateAction<boolean>>;
  handleModalButtonClick: React.MouseEventHandler<HTMLButtonElement>;
};

const FakeModal = (props: ModalProps) => {
  const { onClose } = useDisclosure();
  const handleModalClose = () => {
    props.modalHandler(false);
    onClose();
  };
  return (
    <Modal isOpen={props.isOpen} onClose={handleModalClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader color="viridian.base">{modal.heading}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex flexDirection="column" gap="6">
            <Text color="tints.darkGrey">{modal.content}</Text>

            <Button width="100%" onClick={props.handleModalButtonClick}>
              {modal.ctaLabel}
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const Options = (props: OptionsProps) => {
  const { items, selectedValue, setSelectedValue, ...rest } = props;

  return (
    <Box gap={4} display="flex" flexDirection="column">
      {items.map((item: any, index: number) => (
        <Box
          width="100%"
          background="tints.lightCream"
          borderRadius={'12px'}
          key={`${item.label}-${index}`}
          border="none"
          sx={{
            ...(rest.sx ?? {}),
            ...(selectedValue === item
              ? {
                  boxShadow: (theme) =>
                    `0px 0px 0px 2px inset ${theme.colors.viridian.base}`,
                }
              : {}),
          }}
        >
          <Box
            {...rest}
            display="flex"
            alignItems={'center'}
            data-testid="membership-accordion-button"
            justifyContent="space-between"
            padding={4}
            onClick={() => {
              setSelectedValue(item);
            }}
            role="button"
            cursor="pointer"
          >
            <Box textAlign={'start'}>
              <Text>{item.label}</Text>
              <Text size="sm" color="tints.darkGrey">
                {item.description}
              </Text>
            </Box>
            {
              <Box textAlign={'end'}>
                {item.price.promoText ? (
                  <Text fontSize="2xl" fontWeight="medium">
                    {item.price.promoText}
                  </Text>
                ) : (
                  <Price price={item.price.value} fontWeight={'medium'} />
                )}
                <Text size="sm" color="tints.darkGrey">
                  {item.price.frequency}
                </Text>
              </Box>
            }
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const FooterDrawer = ({
  isOpen,
  onClose,
  content,
  cta,
  price,
  selectedOption,
}: FooterDrawerProps) => (
  <Drawer
    isOpen={isOpen}
    onClose={onClose}
    placement={'bottom'}
    blockScrollOnMount={false} // disable block scroll from chakra due to this bug: https://github.com/chakra-ui/chakra-ui/issues/8261
  >
    {/* NOTE: zIndex index is overridden in order to place Cart on top of the Chat Bubble */}
    <DrawerOverlay zIndex={9997} />
    <DrawerContent
      position="relative"
      gap={0}
      containerProps={{ zIndex: 9998 }}
      minHeight="90vh"
      top="auto !important"
      borderRadius="24px 24px 0 0"
    >
      <DrawerHeader paddingX={[4, 8]} paddingBottom={4}>
        <Heading as="h1" size="md">
          Select options
        </Heading>
        <DrawerCloseButton />
      </DrawerHeader>
      <DrawerBody paddingY={4} paddingX={[4, 8]} overflow="auto">
        {content}
      </DrawerBody>
      <DrawerFooter
        background="tints.lightCream"
        padding={0}
        display={'flex'}
        flexDirection="column"
        alignItems="stretch"
        position="relative"
      >
        <Box
          backgroundColor={'tints.lightCream'}
          borderRadius={'24px 24px 0 0'}
          paddingTop={'20px'}
          paddingBottom={'34px'}
          paddingX={6}
          boxShadow="0px 4px 35px 0px rgba(0, 0, 0, 0.2)"
        >
          <Box
            display={'flex'}
            flexDirection={'row'}
            justifyContent={'space-between'}
            marginBottom={3}
          >
            <Text
              display={'flex'}
              flexDirection={'column'}
              fontWeight={'medium'}
              fontSize={'lg'}
            >
              Subtotal
              <Text color="tints.darkGrey" fontSize={'md'}>
                {selectedOption}
              </Text>
            </Text>
            <Price price={price} />
          </Box>
          {cta}
        </Box>
      </DrawerFooter>
    </DrawerContent>
  </Drawer>
);

const StickyAddToCartBar = ({ cta, price, isVisible }: StickyProps) => (
  <>
    <Box
      visibility={isVisible ? 'visible' : 'hidden'}
      backgroundColor={['tints.lightCream', 'tints.white']}
      paddingX={6}
      paddingY={4}
      top={['68px', null, '58px']}
      left={0}
      position={'fixed'}
      zIndex={2}
      width={'100%'}
      boxShadow={['2xl', 'none']}
      borderBottomRadius={['24px', 0]}
      display={'flex'}
      justifyContent={'space-between'}
      alignItems={'center'}
    >
      <Price price={price} />
      <Button colorScheme="greenCS" onClick={cta?.onClick}>
        {cta?.label}
      </Button>
    </Box>
  </>
);

const FakeProductDescription = () => {
  const sharedButtonProps = createSharedButtonProps(cta?.url || '');
  const { handleMdpCtaClick } = useMdpModals();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isMobile] = useMediaQuery(`(max-width: ${theme.breakpoints.tablet})`);
  const [selectedValue, setSelectedValue] = useState(items[0]);
  const [totalPrice, setTotalPrice] = useState(items[0]?.price?.value);
  const [isFakeDoorModalOpen, toggleFakeDoorModal] = useState(false);

  const { inView, ref, entry } = useInView({
    initialInView: false,
    threshold: 0,
    rootMargin: '-68px 0px 0px 0px',
  });

  const onCtaClick = (event: React.MouseEvent<HTMLButtonElement>, cta: CTA) => {
    const shouldOpenFakeDoorModal =
      !isFakeDoorModalOpen && selectedValue === items[1];

    if (shouldOpenFakeDoorModal) {
      event.preventDefault();
      toggleFakeDoorModal(true);
    } else {
      toggleFakeDoorModal(false);
      handleMdpCtaClick({
        event,
        ctaUrl: cta?.url || '',
      });
    }

    trackCtaClick(cta);
  };

  const trackCtaClick = (cta: CTA) => {
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        sectionHeader: title || 'No title provided',
        contentfulSection: 'FakeProductDescription',
        ctaText: cta?.label || 'No CTA label',
        purchaseType: selectedValue?.price?.frequency,
      },
    });
  };

  useEffect(() => {
    const price = selectedValue?.price.value;

    setTotalPrice(price);
  }, [selectedValue?.price.value]);

  const handleCTAClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (isMobile) {
      event.preventDefault();
      onOpen();
      return;
    }

    onCtaClick(event, cta as CTA);
  };

  const handleFakeDoorModalButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    onCtaClick(event, cta as CTA);
    setSelectedValue(items[0]);
  };

  return (
    <>
      <Box data-testid="hero-product-description" ref={ref}>
        {/* Product Name */}
        <Heading
          as="h1"
          size="xl"
          marginBottom={[2, null, 3]}
          textAlign={['center', null, 'left']}
        >
          {title}
        </Heading>
        {/* Main Price */}
        <Price
          fontSize="5xl"
          price={totalPrice}
          textAlign={['center', null, 'initial']}
        />
        {/* Product Options */}
        <Box marginTop={8} display={['none', null, 'block']}>
          <Options
            items={items}
            selectedValue={selectedValue}
            setSelectedValue={setSelectedValue}
          />
        </Box>
        {/* CTA */}
        <Button
          {...sharedButtonProps}
          width="100%"
          className={cta?.metadata?.metadata_id ?? ''}
          onClick={handleCTAClick}
          marginTop={6}
        >
          {cta?.label}
        </Button>
        {/* Disclaimer with icon */}
        <Flex
          marginTop={[6, null, 4]}
          justifyContent="center"
          gap="2"
          flexWrap="wrap"
        >
          {bulletPoints.map((bulletPoint, index) => (
            <BulletPointWithIcon key={index} {...bulletPoint} />
          ))}
        </Flex>
      </Box>
      {isMobile && (
        <>
          <StickyAddToCartBar
            isVisible={Boolean(entry && !inView)}
            price={totalPrice}
            cta={{
              label: cta?.label,
              onClick: onOpen,
            }}
          />
          <FooterDrawer
            isOpen={isOpen}
            onClose={onClose}
            selectedOption={selectedValue?.label}
            price={totalPrice}
            content={
              <Options
                items={items}
                selectedValue={selectedValue}
                setSelectedValue={setSelectedValue}
              />
            }
            cta={
              <Button
                {...sharedButtonProps}
                width="100%"
                className={cta?.metadata?.metadata_id ?? ''}
                onClick={(event) => {
                  onCtaClick(event, cta as CTA);
                  onClose();
                }}
              >
                {cta?.label}
              </Button>
            }
          />
        </>
      )}
      {isBrowser() &&
        createPortal(
          <FakeModal
            isOpen={isFakeDoorModalOpen}
            modalHandler={toggleFakeDoorModal}
            handleModalButtonClick={handleFakeDoorModalButtonClick}
          />,
          document.body,
        )}
    </>
  );
};

export default FakeProductDescription;
